/** Format a number as Swedish currency: 1 234,56 kr */
export function formatSEK(amount: number): string {
  return (
    new Intl.NumberFormat("sv-SE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + " kr"
  );
}

/** Format YYYY-MM-DD to Swedish readable: 17 mars 2026 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Format YYYY-MM to readable: mars 2026 */
export function formatMonth(monthStr: string): string {
  const [year = "1970", month = "1"] = monthStr.split("-");
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  return date.toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
}

/** Main category display names */
export const categoryLabels: Record<string, string> = {
  food_drink: "Mat & Dryck",
  housing: "Boende",
  transport: "Transport",
  shopping: "Shopping",
  entertainment: "Underhållning",
  health: "Hälsa",
  subscriptions: "Prenumerationer",
  income: "Inkomster",
  savings: "Sparande",
  transfer: "Överföringar",
  other: "Övrigt",
};

/** Subcategory display names */
export const subcategoryLabels: Record<string, string> = {
  // Food & Drink
  groceries: "Matvaror",
  restaurants: "Restauranger",
  cafes: "Caféer",
  fast_food: "Snabbmat",
  delivery: "Matleverans",
  alcohol: "Alkohol",
  // Housing
  rent: "Hyra",
  mortgage: "Bolån",
  electricity: "El",
  internet: "Internet",
  insurance: "Försäkring",
  maintenance: "Underhåll",
  furniture: "Möbler",
  // Transport
  public_transit: "Kollektivtrafik",
  fuel: "Bränsle",
  car: "Bil",
  taxi: "Taxi",
  parking: "Parkering",
  bike: "Cykel/Elsparkcykel",
  // Shopping
  clothes: "Kläder",
  electronics: "Elektronik",
  home: "Hem & Inredning",
  online: "Online-shopping",
  gifts: "Presenter",
  // Entertainment
  cinema: "Bio",
  games: "Spel",
  concerts: "Konserter",
  sports: "Sport & Nöje",
  streaming: "Streaming",
  hobbies: "Hobbyer",
  // Health
  pharmacy: "Apotek",
  doctor: "Läkare",
  dentist: "Tandläkare",
  therapy: "Terapi",
  gym: "Gym",
  wellness: "Friskvård",
  // Subscriptions
  digital: "Digitala tjänster",
  news: "Nyheter",
  software: "Mjukvara",
  membership: "Medlemskap",
  // Income
  salary: "Lön",
  freelance: "Frilans",
  refund: "Återbetalning",
  benefits: "Bidrag",
  interest: "Ränta",
  // Savings
  savings_account: "Sparkonto",
  investments: "Investeringar",
  pension: "Pension",
  // Transfer
  internal: "Intern överföring",
  external: "Extern överföring",
  // Other
  uncategorized: "Okategoriserad",
  fees: "Avgifter",
  cash: "Kontant",
  misc: "Diverse",
};

/** Bank display names */
export const bankLabels: Record<string, string> = {
  swedbank: "Swedbank",
  seb: "SEB",
  nordea: "Nordea",
  handelsbanken: "Handelsbanken",
  lansforsakringar: "Länsförsäkringar",
  "ica-banken": "ICA Banken",
  avanza: "Avanza",
  unknown: "Okänd",
};

/** Format SEK without decimals: 12 345 kr */
export function formatSEK0(amount: number): string {
  return (
    new Intl.NumberFormat("sv-SE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) + " kr"
  );
}

/** Compact SEK: 1,2 mkr / 234 tkr / 950 kr */
export function formatSEKCompact(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "−" : "";
  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toLocaleString("sv-SE", { maximumFractionDigits: 1 })} mkr`;
  }
  if (abs >= 10_000) {
    return `${sign}${Math.round(abs / 1000).toLocaleString("sv-SE")} tkr`;
  }
  return `${sign}${Math.round(abs).toLocaleString("sv-SE")} kr`;
}

/** Account kind display names */
export const accountKindLabels: Record<string, string> = {
  bank: "Bankkonto",
  savings: "Sparkonto",
  cash: "Kontanter",
  property: "Bostad/Fastighet",
  vehicle: "Fordon",
  other_asset: "Övrig tillgång",
  credit_card: "Kreditkort",
  loan: "Lån",
  mortgage: "Bolån",
  other_liability: "Övrig skuld",
};

/** Investment asset type display names */
export const assetTypeLabels: Record<string, string> = {
  stock: "Aktier",
  fund: "Fonder",
  etf: "ETF:er",
  certificate: "Certifikat",
  bond: "Räntor",
  other: "Övrigt",
};

/** Recurring cadence display names */
export const cadenceLabels: Record<string, string> = {
  weekly: "Varje vecka",
  monthly: "Varje månad",
  quarterly: "Varje kvartal",
  yearly: "Varje år",
};

export const transactionTypeLabels: Record<string, string> = {
  expense: "Utgift",
  income: "Inkomst",
  transfer: "Överföring",
  fee: "Avgift",
  refund: "Återbetalning",
  payment: "Betalning",
  card: "Kortköp",
  other: "Annat",
};
